/**
 * Media failure state for the Product Create wizard (B-002 / Repair 4).
 *
 * A successful retry used to clear the whole warning list. Failures now carry a
 * stable identity so only the operation that actually succeeded is resolved and
 * unrelated warnings stay visible.
 *
 * Two distinct failure families are kept apart on purpose:
 *   - `draft_transfer`  — the draft's images did not move onto the created product;
 *   - `channel_media`   — one account's chosen primary image was not applied to
 *                         its listing (also reported per connector+stage by
 *                         `wizard-post-create`, which is the primary record).
 *
 * COVERAGE NOTE (F-09): in the current product there is no production writer for
 * `channel_media`. Channel primary-image failures are owned by `ChannelOutcome`
 * rows and rendered from `channelFailureLines`. The `channel_media` variant is
 * retained deliberately as future-facing storage coverage so a second failure
 * system is never invented; it is NOT active production behaviour and must not
 * be counted as such. The wizard only ever writes `draft_transfer`.
 *
 * This module is pure and performs no I/O.
 */

export type WizardMediaFailureSource = "draft_transfer" | "channel_media";

export interface WizardMediaFailure {
  /** Stable identity of the failed operation. Two failures never share one. */
  id: string;
  name: string;
  reason: string;
  source: WizardMediaFailureSource;
}

/** One draft-media transfer per attempt: the draft is the unit of transfer. */
export function draftTransferFailureId(draftId: string | null | undefined): string {
  const id = typeof draftId === "string" && draftId.trim() ? draftId.trim() : "unsaved";
  return `draft_transfer:${id}`;
}

/** Upsert by identity so a repeated failure does not duplicate its warning. */
export function upsertMediaFailure(
  failures: WizardMediaFailure[],
  failure: WizardMediaFailure,
): WizardMediaFailure[] {
  const index = failures.findIndex((row) => row.id === failure.id);
  if (index === -1) return [...failures, failure];
  const next = [...failures];
  next[index] = failure;
  return next;
}

/**
 * Resolve only the listed identities. Unrelated or unresolved failures are
 * kept, and the same array reference is returned when nothing changed so the
 * caller does not trigger a needless render or draft autosave.
 */
export function resolveMediaFailures(
  failures: WizardMediaFailure[],
  ids: string[],
): WizardMediaFailure[] {
  const target = new Set(ids);
  if (target.size === 0) return failures;
  const next = failures.filter((row) => !target.has(row.id));
  return next.length === failures.length ? failures : next;
}

/**
 * The draft-media transfer that has just succeeded, and nothing else.
 * A channel-media failure must survive a transfer retry.
 */
export function resolveDraftTransferFailures(
  failures: WizardMediaFailure[],
  draftId: string | null | undefined,
): WizardMediaFailure[] {
  return resolveMediaFailures(failures, [draftTransferFailureId(draftId)]);
}

export function unresolvedMediaFailureCount(failures: WizardMediaFailure[]): number {
  return failures.length;
}

/** Stable keys for rendering, so two failures with the same label stay distinct. */
export function mediaFailureRows(failures: WizardMediaFailure[]): { key: string; name: string; reason: string }[] {
  return failures.map((row) => ({ key: row.id, name: row.name, reason: row.reason }));
}